import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

import { SignUpContext } from "../context";
import { useAxios, useCustomFormik } from "../hooks";
import SignButton from "components/SignButton";
import SignInputError from "components/SignInputError";
import StepCaption from "components/StepCaption";
import ExpiredStorage from "expired-storage";
import useComponentDidMount from "hooks/useComponentDidMount";
import { getSecondsString } from "utils/formatters";
import noop from "lodash/noop";
import { useNavigate } from "react-router-dom";

export const expiredStorage = new ExpiredStorage();
export const LS_PHONE_VERIFY_DISABLE = "publisher_validate_phone_disable";
export const RESEND_DISABLE_PERIOD = 60;

export const FormSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .required("This value is required.")
    .test("isValidate", "Invalid phone number", (value) =>
      value ? isValidPhoneNumber(value) : false
    ),
});

export default function PhoneVerifyForm() {
  const navigate = useNavigate();
  const intervalId = useRef<NodeJS.Timeout>();
  const [count, setCount] = useState(
    expiredStorage.getTimeLeft(LS_PHONE_VERIFY_DISABLE) ?? -1
  );
  const { userData, setUserData } = useContext(SignUpContext);
  const { requestApi } = useAxios();
  const [loading, setLoading] = useState(false);
  const formik = useCustomFormik({
    initialValues: {
      phoneNumber: userData.phoneNumber,
    },
    validationSchema: FormSchema,
    onSubmit: (values) => {
      const body = {
        phone_number: values.phoneNumber,
      };
      setLoading(true);
      requestApi("post", "/api/v1/users/send_sms_code", body)
        .then(() => {
          expiredStorage.setItem(
            LS_PHONE_VERIFY_DISABLE,
            "",
            RESEND_DISABLE_PERIOD
          );
          setUserData({
            ...userData,
            ...values,
          });
          navigate("/users/onboarding/check_code");
        })
        .catch(noop)
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    if (count <= 0) {
      stopInterval();
    }
  }, [count]);

  const startInterval = () => {
    const interval = setInterval(() => {
      setCount((prevCount) => prevCount - 1);
    }, 1000);
    intervalId.current = interval;
  };

  const stopInterval = () => {
    if (!intervalId.current) return;
    clearInterval(intervalId.current);
    intervalId.current = undefined;
    setCount(-1);
  };

  useComponentDidMount(() => {
    expiredStorage.clearExpired();
    const timeLeft = expiredStorage.getTimeLeft(LS_PHONE_VERIFY_DISABLE);
    if (!!timeLeft && timeLeft > 0) {
      startInterval();
    }
  });

  return (
    <div>
      <StepCaption
        heading="Let's verify your account"
        subheading="Please enter your mobile number to verify your account"
      />
      <form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <PhoneInput
            defaultCountry="US"
            name="phoneNumber"
            placeholder="(201) 555-0123"
            value={formik.values.phoneNumber}
            className="form-input"
            onChange={(e) => formik.setFieldValue("phoneNumber", e)}
            onBlur={formik.handleBlur}
          />
          {formik.errors.phoneNumber && formik.touched.phoneNumber && (
            <SignInputError
              errorMessage={formik.errors.phoneNumber.toString()}
            />
          )}
        </div>
        <SignButton
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={count > 0 || loading}
          endIcon={
            loading ? (
              <CircularProgress size={12} sx={{ color: "#fff" }} />
            ) : null
          }
        >
          Send Verification Code
        </SignButton>
        {count > 0 && (
          <Button
            variant="text"
            fullWidth
            size="large"
            disabled={true}
            sx={{ textTransform: "none" }}
          >
            {`Resend available in ${getSecondsString(count)}`}
          </Button>
        )}
      </form>
    </div>
  );
}
